import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useInstallPrompt } from "@/hooks/useInstallPrompt";
import {
  Radar, Loader2, Navigation, CheckCircle2, XCircle, MapPin, Power, WifiOff,
  Briefcase, Wallet, UserCog, Download, CloudOff,
} from "lucide-react";

// Wearable Companion HUD — the pilot's PRIMARY field app (installable PWA, launch
// screen). Consumes the event-driven /wearables APIs; one-tap access to Missions,
// Earnings, Profile, Availability + offline action queue with background sync.
const ADV = { accepted: ["en_route", "Start — En route"], en_route: ["on_scene", "Arrived on scene"], on_scene: ["completed", "Complete mission"] };
const QKEY = "cr_hud_queue";
const NEXT_AV = { online: "busy", busy: "offline", offline: "online" };

const readQueue = () => { try { return JSON.parse(localStorage.getItem(QKEY) || "[]"); } catch { return []; } };
const writeQueue = (q) => localStorage.setItem(QKEY, JSON.stringify(q));

export default function PilotHUD() {
  const [feed, setFeed] = useState(null);
  const [err, setErr] = useState(false);
  const [busy, setBusy] = useState(false);
  const [queued, setQueued] = useState(readQueue().length);
  const navigate = useNavigate();
  const { canInstall, promptInstall, isStandalone, isIOS } = useInstallPrompt();

  const load = useCallback(async () => {
    try { const { data } = await api.get("/wearables/feed"); setFeed(data); setErr(false); }
    catch (e) { if (e?.response?.status === 403) setFeed("noaccess"); else setErr(true); }
  }, []);

  const flushQueue = useCallback(async () => {
    let q = readQueue();
    if (!q.length) return;
    const remaining = [];
    for (const item of q) {
      try { await api.post("/wearables/action", item); }
      catch { remaining.push(item); }
    }
    writeQueue(remaining); setQueued(remaining.length);
    if (q.length && !remaining.length) { toast.success("Queued actions synced"); load(); }
  }, [load]);

  useEffect(() => {
    load(); flushQueue();
    const t = setInterval(load, 5000);
    const onOnline = () => flushQueue();
    window.addEventListener("online", onOnline);
    const onMsg = (e) => { if (e.data?.type === "flush-queue") flushQueue(); };
    navigator.serviceWorker?.addEventListener?.("message", onMsg);
    return () => { clearInterval(t); window.removeEventListener("online", onOnline); navigator.serviceWorker?.removeEventListener?.("message", onMsg); };
  }, [load, flushQueue]);

  const enqueue = async (payload) => {
    const q = readQueue(); q.push(payload); writeQueue(q); setQueued(q.length);
    try {
      const reg = await navigator.serviceWorker?.ready;
      await reg?.sync?.register?.("crashready-flush");
    } catch { /* background sync unsupported — retries on 'online' */ }
    toast.info("Offline — action queued, will sync when back online");
  };

  const act = async (dispatch_id, action) => {
    const payload = { dispatch_id, action };
    if (!navigator.onLine) return enqueue(payload);
    setBusy(true);
    try { await api.post("/wearables/action", payload); toast.success(action.replace("_", " ")); await load(); }
    catch (e) {
      if (!e?.response) { await enqueue(payload); }
      else { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Action failed"); }
    }
    finally { setBusy(false); }
  };

  const cycleAvailability = async () => {
    const cur = (feed && feed !== "noaccess" && feed.availability) || "offline";
    const next = NEXT_AV[cur] || "online";
    try { await api.put("/pilots/me/status", { status: next }); setFeed((f) => ({ ...f, availability: next })); toast.success(`You are ${next}`); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed"); }
  };

  const openMaps = (j) => {
    const dest = j.pilot_location ? `${j.pilot_location.lat},${j.pilot_location.lng}` : encodeURIComponent([j.location_city, j.location_state].filter(Boolean).join(", "));
    window.open(`https://www.google.com/maps/dir/?api=1&destination=${dest}`, "_blank");
  };

  if (feed === null) return <Screen><Loader2 className="w-10 h-10 text-cyan-400 animate-spin" /></Screen>;
  if (feed === "noaccess") return <Screen><p className="text-slate-300 text-center text-lg">Approved pilots only.</p></Screen>;
  if (err && !feed) return <Screen><WifiOff className="w-10 h-10 text-slate-500" /><p className="text-slate-400 mt-3">Reconnecting…</p></Screen>;

  const active = (feed.active || [])[0];
  const offer = (feed.offers || [])[0];
  const av = feed.availability || "offline";

  return (
    <div className="min-h-screen bg-slate-950 text-white flex flex-col" data-testid="pilot-hud">
      <div className="flex items-center justify-between px-5 py-3 border-b border-slate-800">
        <span className="flex items-center gap-1.5 text-xs font-bold tracking-widest text-cyan-400"><Radar className="w-4 h-4" /> RESPONSE HUD</span>
        <div className="flex items-center gap-3">
          {queued > 0 && <span className="flex items-center gap-1 text-xs text-amber-400" data-testid="hud-queued"><CloudOff className="w-3.5 h-3.5" /> {queued}</span>}
          <button onClick={cycleAvailability} data-testid="hud-availability" className={`flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-full border ${av === "online" ? "text-emerald-400 border-emerald-500/40" : av === "busy" ? "text-amber-400 border-amber-500/40" : "text-slate-400 border-slate-600"}`}>
            <Power className="w-3.5 h-3.5" /> {av.toUpperCase()}
          </button>
        </div>
      </div>

      {(canInstall || (isIOS && !isStandalone)) && (
        <div className="mx-4 mt-3 rounded-xl bg-cyan-500/10 border border-cyan-500/30 px-4 py-2.5 flex items-center justify-between gap-3" data-testid="hud-install-banner">
          <span className="text-xs text-cyan-200">{isIOS ? "Install: tap Share → Add to Home Screen" : "Install Response as your field app"}</span>
          {canInstall && <button onClick={promptInstall} data-testid="hud-install-btn" className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-cyan-500 text-slate-950 text-xs font-bold"><Download className="w-3.5 h-3.5" /> Install</button>}
        </div>
      )}

      <div className="flex-1 flex flex-col items-center justify-center p-5 gap-5 text-center">
        {active ? (
          <div className="w-full max-w-md" data-testid="hud-active">
            <div className="text-xs uppercase tracking-widest text-cyan-400 mb-1">Active mission</div>
            <div className="text-3xl font-heading font-extrabold flex items-center justify-center gap-2"><MapPin className="w-7 h-7 text-cyan-400" /> {active.location_city || active.location_state || "Scene"}</div>
            <div className="mt-1 text-slate-400 capitalize">{active.status.replace("_", " ")} · <span className="text-emerald-400">${Number(active.fee || 0).toFixed(0)}</span></div>
            <button onClick={() => openMaps(active)} data-testid="hud-navigate" className="mt-5 w-full py-4 rounded-2xl bg-slate-800 text-white text-lg font-bold flex items-center justify-center gap-2 active:scale-[0.98]"><Navigation className="w-5 h-5 text-cyan-400" /> Navigate</button>
            {ADV[active.status] && (
              <button onClick={() => act(active.dispatch_id, ADV[active.status][0])} disabled={busy} data-testid="hud-advance" className="mt-3 w-full py-5 rounded-2xl bg-cyan-500 text-slate-950 text-xl font-extrabold flex items-center justify-center gap-2 active:scale-[0.98] disabled:opacity-50">
                {busy ? <Loader2 className="w-6 h-6 animate-spin" /> : <CheckCircle2 className="w-6 h-6" />} {ADV[active.status][1]}
              </button>
            )}
          </div>
        ) : offer ? (
          <div className="w-full max-w-md" data-testid="hud-offer">
            <div className="text-xs uppercase tracking-widest text-amber-400 mb-1">New offer</div>
            <div className="text-3xl font-heading font-extrabold flex items-center justify-center gap-2"><MapPin className="w-7 h-7 text-amber-400" /> {offer.location_city || offer.location_state || "Scene"}</div>
            <div className="mt-1 text-slate-400">{offer.case_number}</div>
            <button onClick={() => act(offer.dispatch_id, "accept")} disabled={busy} data-testid="hud-accept" className="mt-5 w-full py-5 rounded-2xl bg-emerald-500 text-slate-950 text-xl font-extrabold flex items-center justify-center gap-2 active:scale-[0.98] disabled:opacity-50">
              {busy ? <Loader2 className="w-6 h-6 animate-spin" /> : <CheckCircle2 className="w-6 h-6" />} Accept
            </button>
            <button onClick={() => act(offer.dispatch_id, "decline")} disabled={busy} data-testid="hud-decline" className="mt-3 w-full py-4 rounded-2xl bg-slate-800 text-slate-300 text-lg font-bold flex items-center justify-center gap-2 active:scale-[0.98] disabled:opacity-50">
              <XCircle className="w-5 h-5" /> Decline
            </button>
          </div>
        ) : (
          <div className="text-center" data-testid="hud-idle">
            <Radar className="w-14 h-14 text-slate-700 mx-auto" />
            <p className="mt-4 text-xl font-semibold text-slate-300">Standing by</p>
            <p className="text-slate-500 text-sm mt-1">{av === "online" ? "Waiting for the next dispatch…" : "Tap the status chip above to go Available."}</p>
          </div>
        )}
      </div>

      {/* One-tap field navigation */}
      <div className="grid grid-cols-4 border-t border-slate-800" data-testid="hud-navbar">
        {[
          { key: "missions", label: "Missions", icon: Briefcase, to: "/pilot-portal?tab=missions" },
          { key: "earnings", label: "Earnings", icon: Wallet, to: "/pilot-portal?tab=earnings" },
          { key: "navigate", label: "Navigate", icon: Navigation, action: () => active ? openMaps(active) : offer ? openMaps(offer) : toast.info("No active mission to navigate to") },
          { key: "profile", label: "Profile", icon: UserCog, to: "/pilot-portal?tab=profile" },
        ].map((n) => {
          const Icon = n.icon;
          return (
            <button key={n.key} data-testid={`hud-nav-${n.key}`} onClick={() => n.to ? navigate(n.to) : n.action()}
              className="flex flex-col items-center gap-1 py-3 text-slate-400 hover:text-cyan-300 active:bg-slate-900 transition-colors">
              <Icon className="w-5 h-5" /> <span className="text-[11px] font-medium">{n.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

const Screen = ({ children }) => (
  <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center p-6">{children}</div>
);
